import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label } from "reactstrap";
import { useForm } from "react-hook-form";

const QuestionModal = ({ isOpen, toggle, questionsList, onSave }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localQuestions, setLocalQuestions] = useState([...questionsList]); // Copy of questionList

  const currentQuestion = localQuestions[currentIndex];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: currentQuestion,
  });

  // Reset form when question changes
  useEffect(() => {
    reset(currentQuestion);
  }, [currentQuestion, reset]);

  // Handle option change
  const handleOptionChange = (index, value) => {
    const updatedOptions = [...watch("options")];
    updatedOptions[index].option = value;

    setValue("options", updatedOptions);
    trigger(`options.${index}.option`);

    // Update the localQuestions state
    const updatedQuestions = [...localQuestions];
    updatedQuestions[currentIndex].options = updatedOptions;
    setLocalQuestions(updatedQuestions);
  };

  // Handle correct answer selection
  const handleCorrectChange = (index) => {
    const updatedOptions = watch("options").map((o, i) => ({
      ...o,
      isCorrect: i === index,
    }));

    setValue("options", updatedOptions);

    const updatedQuestions = [...localQuestions];
    updatedQuestions[currentIndex].options = updatedOptions;
    setLocalQuestions(updatedQuestions);
  };

  // Handle question text change
  const handleQuestionChange = (value) => {
    setValue("question", value);
    trigger("question");

    const updatedQuestions = [...localQuestions];
    updatedQuestions[currentIndex].question = value;
    setLocalQuestions(updatedQuestions);
  };

  // Submit the entire question list
  const handleSaveAll = () => {
    console.log("Sending to API:", localQuestions);
    if (onSave) onSave(localQuestions); // callback to parent for API call
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static">
      <ModalHeader toggle={toggle}>
        Question {currentIndex + 1} of {localQuestions.length}
      </ModalHeader>
      <ModalBody>
        <form>
          <Row>
            <Col md="12">
              <div className="form-group">
                <Label className="form-label" htmlFor="question">
                  Question
                </Label>
                <textarea
                  id="question"
                  className="form-control"
                  {...register("question", {
                    required: "Question is required",
                    minLength: { value: 3, message: "At least 3 characters" },
                    maxLength: { value: 500, message: "Max 500 characters" },
                  })}
                  value={watch("question")}
                  onChange={(e) => handleQuestionChange(e.target.value)}
                />
                {errors.question && <span className="invalid">{errors.question.message}</span>}
              </div>
            </Col>

            {watch("options")?.map((opt, index) => (
              <React.Fragment key={index}>
                <Col md="10 mt-2">
                  <div className="form-group">
                    <Label className="form-label">{`Option ${index + 1}`}</Label>
                    <input
                      type="text"
                      className="form-control"
                      value={watch(`options.${index}.option`)}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                    />
                  </div>
                </Col>

                <Col md="2" className="d-flex align-items-end">
                  <div className="form-group">
                    <input
                      type="radio"
                      name="correctOption"
                      checked={watch("options")[index]?.isCorrect || false}
                      onChange={() => handleCorrectChange(index)}
                      className="custom-radio me-2"
                    />
                    <Label className="form-label">Correct Answer</Label>
                  </div>
                </Col>
              </React.Fragment>
            ))}
          </Row>
        </form>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))}>
          Previous
        </Button>
        <Button color="primary" onClick={() => setCurrentIndex((prev) => Math.min(prev + 1, localQuestions.length - 1))}>
          Next
        </Button>
        <Button color="success" onClick={handleSaveAll}>
          Save All
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default QuestionModal;
